import { mapToStaffPosition } from './noteMapping.js';

// Enhanced note type determination using duration and intensity
export const getNoteType = (duration, intensity = 0, globalMaxIntensity = 1, hasStress = false) => {
  let noteType, stemHeight, noteHeadClass;

  // Adjust duration based on intensity - stronger intensity = longer perceived duration
  const intensityFactor = 1 + (intensity / globalMaxIntensity) * 0.3;
  const adjustedDuration = duration * intensityFactor;

  // Cap the adjusted duration to prevent excessively long notes
  const cappedDuration = Math.min(adjustedDuration, 1.5);

  if (cappedDuration > 0.8) {
    noteType = 'whole';
    stemHeight = 0;
    noteHeadClass = 'w-5 h-4';
  } else if (cappedDuration > 0.5) {
    noteType = 'half';
    stemHeight = hasStress ? 28 : 24;
    noteHeadClass = 'w-4 h-3';
  } else if (cappedDuration > 0.3) {
    noteType = 'quarter';
    stemHeight = hasStress ? 24 : 20;
    noteHeadClass = 'w-4 h-3';
  } else {
    noteType = 'eighth';
    stemHeight = hasStress ? 20 : 16;
    noteHeadClass = 'w-3 h-2';
  }

  return { noteType, stemHeight, noteHeadClass, adjustedDuration: cappedDuration };
};

// Helper function to calculate durations within a speech segment
export const calculateNoteDurationsWithinSegment = (localMaxima, segmentStart, segmentEnd) => {
  const noteDurations = [];
  const segmentDuration = segmentEnd - segmentStart;
  
  for (let i = 0; i < localMaxima.length; i++) {
    const prevTime = i === 0 ? segmentStart : localMaxima[i - 1].time;
    const currentTime = localMaxima[i].time;
    const nextTime = i === localMaxima.length - 1 ? segmentEnd : localMaxima[i + 1].time;
    
    const segmentStartBoundary = i === 0 ? segmentStart : (prevTime + currentTime) / 2;
    const segmentEndBoundary = i === localMaxima.length - 1 ? segmentEnd : (currentTime + nextTime) / 2;
    
    const duration = Math.max(0.1, Math.min(segmentEndBoundary - segmentStartBoundary, segmentDuration));
    
    noteDurations.push({
      start: segmentStartBoundary,
      end: segmentEndBoundary,
      duration: duration,
      time: currentTime
    });
  }
  
  return noteDurations;
};

// Helper function to create a single note for a speech segment
export const createSegmentNote = (speechSegment, segmentIndex, allNotes, totalSegmentDuration, currentTime, globalMinIntensity, globalMaxIntensity) => {
  const segmentCenter = speechSegment.start + (speechSegment.duration / 2);
  const absolutePosition = (segmentCenter / totalSegmentDuration) * 100;
  
  const { staff, noteLevel, musicalNote, frequency, line } = mapToStaffPosition(speechSegment.averagePitch);
  const cappedDuration = Math.min(speechSegment.duration, 1.0);
  const { noteType, stemHeight, noteHeadClass } = getNoteType(
    cappedDuration,
    speechSegment.averageIntensity, 
    globalMaxIntensity
  );
  
  const stemDirection = line < 2 ? 'up' : 'down';
  const isActive = currentTime >= speechSegment.start && currentTime <= speechSegment.end;

  allNotes.push({
    staff,
    noteLevel,
    noteType,
    stemHeight,
    stemDirection,
    noteHeadClass,
    position: absolutePosition,
    duration: cappedDuration,
    startTime: speechSegment.start,
    endTime: speechSegment.end,
    isActive,
    pitch: speechSegment.averagePitch,
    intensity: speechSegment.averageIntensity,
    musicalNote,
    targetFrequency: frequency,
    linePosition: line,
    hasStress: speechSegment.peakIntensity > (globalMinIntensity + (globalMaxIntensity - globalMinIntensity) * 0.6),
    isLocalMaxima: false,
    segmentIndex,
    time: segmentCenter,
    isVoiced: true
  });
};